import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import pathnames from "routes/pathnames";

import api from "services/api";

import getQueryParams from "common/get-query-params";
import encodeQueryParams from "common/encode-query-params";
import serveRequestErrors from "common/serve-request-errors";

import PageArticlesDto, { PageArticlesTableQueryParamsDto } from "dto/pages/articles/page-articles-dto";
import ErrorResponseDto from "dto/services/error-response-dto";

import Button from "components/app-button";
import AppHeading from "components/app-heading";
import ArticlesTable from "components/pages/page-articles/app-articles-table";

const PageArticles = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [articles, setArticles] = useState<PageArticlesDto>();
	const [isFetching, setIsFetching] = useState(false);
	const memoSetSearchParams = useRef(setSearchParams);
	const queryParams = useRef(getQueryParams<PageArticlesTableQueryParamsDto>(searchParams, { page: 0 }));

	const onHandleGetArticles = useCallback(async () => {
		setIsFetching(true);

		try {
			const { page } = queryParams.current;

			const payload = { size: 10, page };

			const response = await api.get.articles.articles("ascending", payload);

			setArticles(response.data?.data?.list);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		} finally {
			setIsFetching(false);
		}
	}, []);

	//prettier-ignore
	const handlePageChange = useCallback((page: number) => {
        queryParams.current.page = page;

        memoSetSearchParams.current(encodeQueryParams(queryParams.current));

        onHandleGetArticles();
    }, [onHandleGetArticles]);

	const navigateToAddNew = useCallback(() => {
		navigate(`${pathnames.article}create`);
	}, [navigate]);

	useEffect(() => {
		onHandleGetArticles();
	}, [onHandleGetArticles]);

	return (
		<div className="page-articles">
			<div className="articles">
				<AppHeading title="Articles Learning Centre">
					<AppHeading.Divider />
				</AppHeading>

				<div className="articles__button-container">
					<Button label="Add New" onClick={navigateToAddNew} />
				</div>

				<ArticlesTable articles={articles?.content || []} currentPage={articles?.number || 0} totalPages={articles?.totalPages || 0} onPageChange={handlePageChange} fetching={isFetching} />
			</div>
		</div>
	);
};

export default PageArticles;
